import { useMantineColorScheme } from "@mantine/core";
import { useCallback } from "react";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";

import type { SharedWidgetAppearance } from "./customization-types";

export interface SharedWidgetConfig {
  config: unknown;
  appearance: SharedWidgetAppearance;
  updateAppearance: (updates: Partial<SharedWidgetAppearance>) => void;
  resetConfig: () => void;
  isLoading: boolean;
}

const SharedWidgetConfigContext = createContext<SharedWidgetConfig | null>(null);

export function SharedWidgetConfigProvider({
  value,
  children,
}: {
  value: SharedWidgetConfig;
  children: ReactNode;
}) {
  return (
    <SharedWidgetConfigContext.Provider value={value}>
      {children}
    </SharedWidgetConfigContext.Provider>
  );
}

export function useSharedWidgetConfig(): SharedWidgetConfig {
  const ctx = useContext(SharedWidgetConfigContext);
  if (!ctx) {
    throw new Error("useSharedWidgetConfig must be called inside a widget config provider");
  }
  return ctx;
}

export function useSharedWidgetAppearance() {
  const { appearance, updateAppearance: rawUpdate } = useSharedWidgetConfig();
  const { setColorScheme } = useMantineColorScheme();

  const updateAppearance = useCallback(
    (updates: Partial<SharedWidgetAppearance>) => {
      rawUpdate(updates);

      if (updates.theme) {
        setColorScheme(updates.theme);
        localStorage.setItem("mantine-color-scheme", updates.theme);
      }

      if (updates.currency) {
        localStorage.setItem("widget-currency", updates.currency);
      }
    },
    [rawUpdate, setColorScheme],
  );

  return { appearance, updateAppearance };
}
